import { cva, cx, type VariantProps } from 'cva'
import type { ImageProps } from 'next/image'
import Image from 'next/image'
import type { FC } from 'react'
import type { ImageData } from '../../frontend-types'
import { Corner } from './Corner'
import { PlaceholderImage } from './PlaceholderImage'

export type ImageWithCornerProps = Pick<ImageProps, 'sizes'> & {
	image?: ImageData
	aspectRatio?: VariantProps<typeof imageWithCornerVariants>['aspectRatio']
	cornerColour?: 'white' | 'blue'
	className?: string
	hover?: boolean
}

export const imageWithCornerVariants = cva({
	base: 'relative w-full overflow-hidden',
	variants: {
		aspectRatio: {
			'aspect-video': 'aspect-video rounded-l-sm rounded-br-sm',
			'aspect-card-topic':
				'tablet-p:aspect-card-topic rounded-l-sm h-full tablet-p:h-auto tablet-p:rounded-bl-none',
			'aspect-card-video': 'aspect-card-video rounded-tl-sm',
			'aspect-image-gallery': 'aspect-image-gallery rounded-tl-sm',
			'aspect-profile': 'aspect-profile rounded-l-sm rounded-br-sm',
			'aspect-none': 'aspect-none rounded-l-sm rounded-br-sm',
		},
	},
})

export const cornerColourVariants = cva({
	variants: {
		cornerColour: {
			white: 'text-white',
			blue: 'text-blue-tint-10',
		},
	},
	defaultVariants: {
		cornerColour: 'white',
	},
})

const hoverStateClasses = 'group-hover:scale-110 transition-transform ease-in-out duration-300'

export const ImageWithCorner: FC<ImageWithCornerProps> = ({
	image,
	aspectRatio = 'aspect-video',
	hover = false,
	sizes,
	className,
}) => {
	return (
		<div className={cx('relative w-full overflow-hidden', className)}>
			<Corner className="text-theme-bg" />
			<div className={imageWithCornerVariants({ aspectRatio })}>
				{image ? (
					aspectRatio === 'aspect-none' ? (
						<Image
							src={image.src}
							alt={image.alt}
							className={cx('h-auto w-full object-cover', hover && hoverStateClasses)}
							width={image.width}
							height={image.height}
						/>
					) : (
						<Image
							src={image.src}
							alt={image.alt}
							fill
							className={cx('object-cover', hover && hoverStateClasses)}
							sizes={sizes}
						/>
					)
				) : (
					<PlaceholderImage />
				)}
			</div>
		</div>
	)
}
